import React, {useContext, useState} from "react";
import {Button, Dialog, DialogContent, Grid, IconButton, InputAdornment, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import {HighlightOffRounded, InfoRounded} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useForm, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useMutation} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {isBrowser, isMobile} from "react-device-detect";
import {AxiosError} from "axios";
// @ts-ignore
import QrCodeScanner from "react-qr-scanner";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import ContentBody from "../../common/content-body/ContentBody";
import CustomLoader from "../../../../util/component/CustomLoader";
import {IRaVehicleIdentificationForm} from "../../models/models";
import {FormTextField } from "../../common/form-fields-wrappers";
import SectionHeader from "../../../../util/component/SectionHeader";
import {RA_VEHICLE_IDENTIFICATION_FORM_VALIDATION_SCHEMA, RA_VEHICLE_IDENTIFICATION_FORM_INITIAL_VALUE} from "../config";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {fetchSearchRaByPlate, fetchSearchRaByRaIdCode} from "../services/http.services";
import NotAllowedDialog from "../../../authentication/component/NotAllowedDialog";
import {clearCheckOutInSessionStorage} from "../services/sessionStorageManager";

const RaVehicleIdentification = ({recoveryMode}: {recoveryMode?: boolean}) => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const navigate = useNavigate();
    const {type} = useParams(); //params.type
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [state, setState] = useState<any>({
        delay: null,
        field: null,
        cameraId: undefined
    });

    const raVehicleIdentificationForm = useForm<IRaVehicleIdentificationForm>({
        defaultValues: RA_VEHICLE_IDENTIFICATION_FORM_INITIAL_VALUE,
        resolver: yupResolver(
            RA_VEHICLE_IDENTIFICATION_FORM_VALIDATION_SCHEMA
        )
    });

    const handleScan = (data: any) => {
        if (!!data && !!state.field) {
            raVehicleIdentificationForm.setValue(state.field, data.text);
            raVehicleIdentificationForm.setValue(state.field === 'ra' ? 'plate' : 'ra', '');
            setState({
                delay: null,
                field: null,
                cameraId: undefined
            });
        }
    }

    const handleError = (err: any) => {
        console.log(err);
    }

    const goToCheck = (raId: string) => {
        if (!!recoveryMode) {
            navigate(`/network/recovery-${type}/${raId}`);
        } else {
            navigate(`/network/${type}/${raId}`);
        }
    }

    const resetFormField = (e: React.SyntheticEvent, field: 'ra' | 'plate') => {
        raVehicleIdentificationForm.resetField(field);
    }

    const viewErrorMessage = (message: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    /** Verify RA by RaIdCode */
    const fetchSearchRaByRaIdCodeMutation = useMutation(
        fetchSearchRaByRaIdCode,
        {
            onSuccess: (response: any) => {
                clearCheckOutInSessionStorage();
                if (!response.executed) {
                    goToCheck(response.idRentalAgreement);
                } else {
                    viewErrorMessage('raCheckOutIn.raVehicleIdentification.message.error.executed');
                }
            },
            onError: (error: AxiosError) => {
                let mess = '';
                // @ts-ignore
                if (error.response?.status === 404 && error.response?.data?.errorDetails === "\"ERROR_ID_RA_NOT_FOUND\"") {
                    mess = "common.error.notExistingRa"
                }
                // @ts-ignore
                else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\""){
                    mess = "common.error.errorInconsistentRaState"
                }
                // @ts-ignore
                else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_RA_CHECKOUT_TIME\""){
                    mess = "common.error.errorCheckOutTimeRA"
                }// @ts-ignore
                else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_RA_TO_RECONCILE\""){
                    mess = "common.error.errorRaToReconcile"
                }
                else{
                    mess = 'translation:error.tryLater';
                }
                viewErrorMessage(mess);
            }
        });

    /** Verify RA by Plate */
    const fetchSearchRaByPlateMutation = useMutation(
        fetchSearchRaByPlate,
        {
            onSuccess: (raId: any) => {
                fetchSearchRaByRaIdCodeMutation.mutate({id: raId, type} as any);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    const handleContinue = () => {
        const formValues = raVehicleIdentificationForm.getValues();

        if (!!formValues.ra) {
            fetchSearchRaByRaIdCodeMutation.mutate({id: formValues.ra, type} as any);
        }
        else if (!!formValues.plate) {
            fetchSearchRaByPlateMutation.mutate({plate: formValues.plate, type} as any);
        }
    }

    const handleClose = () => {
        setState({
            delay: null,
            field: null,
            cameraId: undefined
        });
    }

    const handleScanQrCode = async(e: React.SyntheticEvent, field: string) => {
        const constraints = {
            video: {
                facingMode: 'environment',
                deviceId: '',
            }
        };

        await navigator.mediaDevices.getUserMedia(constraints);
        navigator.mediaDevices.enumerateDevices()
            .then((devices) => {
                let mainBackCameras = devices.filter(
                    (device) => device.kind === 'videoinput' && (device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('posteriore'))
                );
                setState({
                    delay: 100,
                    field: field,
                    cameraId: mainBackCameras.length > 0 ? mainBackCameras[0].deviceId : undefined
                });
            })
            .catch((error) => {
                handleError(error);
            });
    }

    // @ts-ignore
    return (
        <>
            {type === 'check-out' ?
            <SectionHeader title={t("raCheckOutIn.raVehicleIdentification.header.title")} /> :
            <SectionHeader title={t("raCheckOutIn.raVehicleIdentification.header.titleIn")} />}

            {isBrowser ?
                <NotAllowedDialog title={t("raCheckOutIn.label.operationNotAllowed")} message={t("raCheckOutIn.label.switchToMobile")}/>
                :
                <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <FormProvider {...raVehicleIdentificationForm}>
                        <Grid container columnSpacing={2}>
                            {/* Header */}
                            <Grid item xs={12} sx={{display: "flex", justifyContent: "center"}}>
                                <Typography variant={"h6"}>
                                    {t("raCheckOutIn.raVehicleIdentification.component.title")}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mb: theme.spacing(2)}}>
                                <Typography variant={"bodyLarge"}>
                                    {t("raCheckOutIn.raVehicleIdentification.component.subtitle")}
                                </Typography>
                            </Grid>

                            <Grid container item xs={12} md={6} sx={{display: "flex", justifyContent: "center"}}>
                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", mx: theme.spacing(1)}}>
                                    <FormTextField
                                        name='ra'
                                        label={t("raCheckOutIn.raVehicleIdentification.form.label.ra")}
                                        control={raVehicleIdentificationForm.control}

                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={(e: React.SyntheticEvent) => resetFormField(e, 'ra')}>
                                                        <HighlightOffRounded />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", alignItems: "center", mt: theme.spacing(2)}}>
                                    <Typography variant={"bodySmall"}>
                                        {t("raCheckOutIn.raVehicleIdentification.message.help.scanQrC")}
                                    </Typography>
                                    <Tooltip title={t("raCheckOutIn.raVehicleIdentification.message.help.scanQrC")}>
                                        <InfoRounded fontSize={"small"} sx={{mx: theme.spacing(0.5)}}/>
                                    </Tooltip>
                                </Grid>

                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
                                    <Button color={"accent"} disabled={!isMobile} onClick={(e: React.SyntheticEvent) => handleScanQrCode(e, 'ra')}>
                                        {t('raCheckOutIn.raVehicleIdentification.button.scanQrC')}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid container item xs={12} md={6} >
                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", mx: theme.spacing(1)}}>
                                    <FormTextField
                                        name='plate'
                                        label={t("raCheckOutIn.raVehicleIdentification.form.label.plate")}
                                        control={raVehicleIdentificationForm.control}
                                        inputProps ={{ style : {textTransform: "uppercase"} }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={(e: React.SyntheticEvent) => resetFormField(e, 'plate')}>
                                                        <HighlightOffRounded />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", alignItems: "center", mt: theme.spacing(2)}}>
                                    <Typography variant={"bodySmall"}>
                                        {t("raCheckOutIn.raVehicleIdentification.message.help.scanQrC")}
                                    </Typography>
                                    <Tooltip title={t("raCheckOutIn.raVehicleIdentification.message.help.scanQrC")}>
                                        <InfoRounded fontSize={"small"} sx={{mx: theme.spacing(0.5)}}/>
                                    </Tooltip>
                                </Grid>

                                <Grid xs={12} sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
                                    <Button color={"accent"} disabled={!isMobile} onClick={(e: React.SyntheticEvent) => handleScanQrCode(e, 'plate')}>
                                        {t('raCheckOutIn.raVehicleIdentification.button.scanQrC')}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} sx={{display: "flex", justifyContent: "center", mt: theme.spacing(4)}}>
                                <Button color={"accent"}
                                        disabled={!['check-in', 'check-out'].includes(type as string)}
                                        onClick={raVehicleIdentificationForm.handleSubmit(handleContinue)} >
                                    {t('raCheckOutIn.raVehicleIdentification.button.continue')}
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Paper>
            </ContentBody>
            }
            <Dialog onClose={handleClose} open={!!state.delay && state.cameraId !== undefined} fullWidth={true} maxWidth={'xs'}>
                <DialogContent>
                    <QrCodeScanner
                        delay={state.delay}
                        style={{
                            height: '100%',
                            width: '100%',
                        }}
                        constraints={{
                            audio: false,
                            video: {
                                deviceId: state.cameraId
                            }
                        }}
                        onError={handleError}
                        onScan={handleScan}
                    />
                </DialogContent>
            </Dialog>

            <CustomLoader loading={fetchSearchRaByRaIdCodeMutation.isLoading || fetchSearchRaByPlateMutation.isLoading}/>
        </>
    );
}

export default RaVehicleIdentification;